import { Button, Flex, Text } from "@mantine/core";
import { type JSX, useState } from "react";

import type { EligibleProduct, IntegratedSalesSteps } from "../../../../types";

import { postDeclination } from "../../../../api/declination";
import { useSalesContext } from "../../../../sales-context/SalesContext";
import {
    useAnalytics,
    withPolymorphicClickAnalytics,
} from "../../../../shared-utils/analytics/withPolymorphicClickAnalytics";
import { useIntegratedFlowStore, useProductOfferStore } from "../../../../stores";
import { SaleProgress } from "../../../../types";
import { getEnrollmentEntryStep } from "../../utils/getEnrollmentEntryStep";
import { RightArrowIcon } from "../Icons";
import styles from "./SalesFooter.module.css";

interface ContinueButtonProps {
    text?: string;
    onClick?: () => Promise<void>;
    nextStep?: IntegratedSalesSteps;
    isDisabled?: boolean;
}

interface SalesFooterProps {
    product?: EligibleProduct;
    onBack?: () => void;
    continueButtonProps?: ContinueButtonProps;
    buttonSlot?: JSX.Element;
    isGuidedBrowsing?: boolean;
}

export function SalesFooter({
    product,
    onBack,
    continueButtonProps: {
        text = "Continue",
        nextStep = getEnrollmentEntryStep(product),
        isDisabled = false,
        onClick,
    } = {},
    buttonSlot,
    isGuidedBrowsing,
}: SalesFooterProps): JSX.Element {
    const {
        sessionId,
        logger,
        partner,
        analytics,
        taskType,
        apiClients,
        isSimulatedVoiceTask: isExpertUniversitySimulatedVoiceTask,
    } = useSalesContext();
    const hideContinueButton = isGuidedBrowsing && !onClick && nextStep === "SendLinkStep";
    // In training mode, we don't want to allow the user to proceed with enrollment past
    // the initial eligibility step, so we check for isSimulatedVoiceTask to disable those buttons
    useAnalytics();
    const AnalyticsButton = withPolymorphicClickAnalytics(Button<"button">, "ButtonClick");
    const [declining, setDeclining] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { isVoice, isChat } = taskType;
    if (!isVoice && !isChat) {
        throw new Error("Active task is not voice or chat task");
    }

    const updateProduct = useProductOfferStore((state) => state.updateProduct);
    const updateSelectedProduct = useProductOfferStore((state) => state.updateSelectedProduct);
    const integratedStep = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));
    const updateIntegratedStep = useIntegratedFlowStore((state) => state.updateIntegratedStep);

    const decline = async () => {
        try {
            if (product) {
                await postDeclination(apiClients.sales, logger, analytics.dispatcher, {
                    sessionId,
                    partner,
                    productOfferId: product.id,
                    productSku: product.productSku,
                });
                updateProduct(sessionId, product.id, { saleProgress: SaleProgress.Declined });
            }
            setDeclining(false);
        } catch {
            setDeclining(false);
        }
    };

    const onContinue = async () => {
        void analytics.dispatcher
            .withExtra({ nextStep: integratedStep })
            .dispatchBusinessEvent("ButtonClicked_ContinueToDisclosures");
        if (onClick) {
            setIsLoading(true);
            try {
                await onClick?.();
            } catch {
                /* do nothing */
            } finally {
                setIsLoading(false);
            }
        } else {
            updateSelectedProduct(sessionId, product);
            updateIntegratedStep(sessionId, nextStep);
        }
    };

    return (
        <Flex className={styles.footerBox}>
            <Flex align={"center"} gap={"xs"}>
                {onBack && (
                    <Button
                        onClick={onBack}
                        size="xs"
                        variant="transparent"
                        color="dark.1"
                        fz="sm"
                        leftSection={<RightArrowIcon height="12" width="12" x="-1" />}
                    >
                        Back
                    </Button>
                )}
                {buttonSlot}
            </Flex>

            <Flex flex={1} gap={"xs"}>
                <AnalyticsButton
                    flex={hideContinueButton ? "0 0 auto" : 1}
                    analytics={{
                        actionId: "EnrollmentDeclineButton",
                        eventData: {
                            nextStep: integratedStep,
                        },
                    }}
                    color="white"
                    data-testid="enrollment-decline-button"
                    size="sm"
                    loading={declining}
                    disabled={
                        !product ||
                        product.saleProgress !== SaleProgress.InProgress ||
                        isExpertUniversitySimulatedVoiceTask
                    }
                    onClick={() => {
                        setDeclining(true);
                        void decline();
                    }}
                    variant="outline"
                >
                    <Text td="underline" size="xs">
                        Decline
                    </Text>
                </AnalyticsButton>

                {!hideContinueButton && (
                    <Button
                        flex={2}
                        disabled={declining || !product || isDisabled || isExpertUniversitySimulatedVoiceTask}
                        className={styles.actionText}
                        color={isChat ? "primary.6" : "primary.5"}
                        fz="xs"
                        size="sm"
                        data-testid="continue-to-disclosures"
                        loading={isLoading}
                        onClick={() => void onContinue()}
                    >
                        <Text fz="xs" c={isDisabled ? "var(--mantine-color-dark-3)" : "var(--mantine-color-white)"}>
                            {text}
                        </Text>
                    </Button>
                )}
            </Flex>
        </Flex>
    );
}
